import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  // Base styles: Display font (Space Grotesk), semibold weight, 150ms transitions
  "inline-flex items-center justify-center gap-2 whitespace-nowrap font-display font-semibold ring-offset-background transition-all duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-60 disabled:bg-zinc-800 disabled:text-zinc-600 disabled:border-zinc-700 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // PRIMARY BUTTON - Gradient green-to-cyan with glow
        default: "btn-primary text-zinc-950 rounded-xl hover:-translate-y-px active:translate-y-0 active:shadow-none",

        // SECONDARY BUTTON - Zinc-800 with border
        secondary: "bg-zinc-800 border border-zinc-700 text-white rounded-xl hover:bg-zinc-700 hover:border-zinc-600 active:bg-zinc-800 active:border-electric",

        // TEXT/LINK BUTTON - Transparent with white text (white -> cyan on hover -> darker cyan active)
        link: "btn-text bg-transparent text-white font-medium hover:text-[#00D4FF] active:text-[#00BFE6]",

        // BADGE/TAG BUTTON - Pill with green tint (#00FF88), mono font, white text on hover
        badge: "btn-badge font-mono text-xs uppercase tracking-wide text-volt rounded-full hover:text-white",

        // SMALL ACTION BUTTON - Compact emerald
        small: "bg-emerald-500 text-zinc-950 rounded-lg hover:bg-emerald-600 hover:shadow-[0_0_16px_rgba(34,197,94,0.3)] active:bg-emerald-700 active:shadow-none",

        // DESTRUCTIVE - Red variant
        destructive: "bg-destructive text-destructive-foreground rounded-xl hover:bg-destructive/90 shadow-sm",

        // OUTLINE - Border only, white text on hover
        outline: "border border-input bg-background text-foreground rounded-xl hover:bg-accent/10 hover:text-white hover:border-accent active:text-white",

        // GHOST - Minimal/transparent, white text on hover
        ghost: "rounded-xl hover:bg-white/5 hover:text-white active:text-white",

        // HERO - Large CTA variant (legacy, now uses primary styling)
        hero: "btn-primary text-zinc-950 rounded-xl shadow-lg hover:-translate-y-0.5 active:translate-y-0 active:shadow-none",

        // HERO OUTLINE - Large CTA outline variant, dark text on hover for contrast with primary bg
        "hero-outline": "border-2 border-primary bg-transparent text-primary rounded-xl hover:bg-primary hover:text-zinc-950 shadow-sm hover:shadow-volt-sm active:text-zinc-950",

        // ZAP BUTTON - Hero action with shimmer animation
        zap: [
          "relative overflow-hidden rounded-xl",
          "btn-primary text-zinc-950 font-bold",
          "shadow-volt hover:shadow-volt-lg",
          "hover:-translate-y-0.5 active:translate-y-0",
          "before:absolute before:inset-0",
          "before:bg-gradient-to-r before:from-transparent before:via-white/20 before:to-transparent",
          "before:translate-x-[-200%] hover:before:translate-x-[200%]",
          "before:transition-transform before:duration-700 before:ease-in-out",
        ].join(" "),

        // ELECTRIC - Cyan accent variant (dark text on bright cyan bg)
        electric: "bg-accent text-zinc-950 rounded-xl hover:bg-accent/90 shadow-electric hover:shadow-lg",
      },
      size: {
        // Default: 24px horizontal, 12px vertical padding, 16px text
        default: "h-12 px-6 py-3 text-base",
        // Small: 16px horizontal, 8px vertical padding, 14px text
        sm: "h-9 px-4 py-2 text-sm",
        // Large: Larger padding
        lg: "h-14 px-8 py-4 text-base",
        // XL: Extra large for hero sections
        xl: "h-16 px-10 py-5 text-base font-bold",
        // Hero size: Main CTAs
        hero: "h-14 px-12 py-3 text-lg font-bold tracking-tight",
        // Icon only
        icon: "h-10 w-10",
        // Badge size: 12px horizontal, 6px vertical
        badge: "h-7 px-3 py-1.5 text-xs",
        // Small action: 16px horizontal, 8px vertical, 14px text
        action: "h-8 px-4 py-2 text-sm",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
